// Логика рекламной очереди киоска без React: какие ролики играют в каком
// месте, с какого продолжать, когда показ засчитан, сколько кругов крутить.
//
// Справедливая очередь. Курсор хранит ключ последнего ЗАСЧИТАННОГО ролика
// отдельно для заставки и для печати (localStorage). Следующий показ
// начинается с ролика после него: если на прошлой печати успели первый и
// второй, эта начнётся с третьего. Курсор — ключ ролика, а не индекс: после
// добавления или удаления роликов очередь не сбивается. Если сам ролик
// курсора удалили, продолжаем с первого, стоявшего в очереди после него.

export const PLACEMENTS = { idle: "idle", print: "print" };

// Видео засчитывается, если досмотрено хотя бы до этой доли длины.
export const VIDEO_COUNT_RATIO = 0.7;
// Фото: небольшой допуск на неточность таймера.
const IMAGE_TOLERANCE_MS = 250;
// Меньше этого прерванный показ в статистику не пишем: это не показ, а мелькание.
export const MIN_REPORTED_MS = 1000;
export const DEFAULT_DISPLAY_SECONDS = 8;

const CURSOR_KEY = (placement) => `adCursor:${placement}`;

export const adKey = (ad) => (ad?.creative_id != null ? `c${ad.creative_id}` : `r${ad?.id}`);

export const isImage = (ad) => ad?.media_type === "image";

export const displayMs = (ad) =>
    Math.max(3, Number(ad?.display_seconds) || DEFAULT_DISPLAY_SECONDS) * 1000;

const orderOf = (ad) => (ad?.order == null ? Infinity : ad.order);

// Плейлист места показа в порядке очереди. Ответ старого сервера (без флагов
// мест) — это заставка: так реклама работала до экрана печати.
export function playlistFor(ads, placement) {
    const list = Array.isArray(ads) ? ads : [];
    return list
        .filter((ad) => ad && ad.ad_path)
        .filter((ad) =>
            placement === PLACEMENTS.print
                ? ad.show_print === true
                : ad.show_idle !== false,
        )
        .sort((a, b) => orderOf(a) - orderOf(b) || (a.id ?? 0) - (b.id ?? 0));
}

function readStorage(key) {
    try {
        return JSON.parse(localStorage.getItem(key));
    } catch {
        return null;
    }
}

function writeStorage(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Хранилище недоступно — очередь начнётся с начала, это не авария.
    }
}

export const loadCursor = (placement) => readStorage(CURSOR_KEY(placement));

export function saveCursor(placement, ad) {
    writeStorage(CURSOR_KEY(placement), { key: adKey(ad), order: ad?.order ?? null });
}

// Индекс ролика, с которого начинать.
export function startIndex(list, cursor) {
    if (!list.length || !cursor) return 0;
    const at = list.findIndex((ad) => adKey(ad) === cursor.key);
    if (at !== -1) return (at + 1) % list.length;
    if (cursor.order != null) {
        const next = list.findIndex((ad) => orderOf(ad) > cursor.order);
        if (next !== -1) return next;
    }
    return 0;
}

// Засчитан ли показ. ended — видео доиграло до конца.
export function isCounted(ad, watchedMs, { ended = false, durationMs = null } = {}) {
    if (isImage(ad)) return watchedMs >= displayMs(ad) - IMAGE_TOLERANCE_MS;
    if (ended) return true;
    const duration = durationMs || Number(ad?.duration_ms) || 0;
    return duration > 0 && watchedMs >= duration * VIDEO_COUNT_RATIO;
}

// Сколько кругов по очереди крутит заставка, прежде чем закрыться.
// null/undefined — «авто», прежняя формула киоска; 0 — до касания экрана.
export function idleRounds(setting, count) {
    if (setting === 0) return Infinity;
    if (Number.isInteger(setting) && setting > 0) return setting;
    if (count === 2) return 5;
    if (count >= 3) return 4;
    return 3;
}

export const DEFAULT_AD_SETTINGS = Object.freeze({
    idle_delay_sec: 60,
    idle_rounds: null,
    print_enabled: true,
});

export function normalizeSettings(settings) {
    const s = settings || {};
    const delay = Number(s.idle_delay_sec);
    return {
        idle_delay_sec: Number.isFinite(delay) && delay >= 5 ? delay : DEFAULT_AD_SETTINGS.idle_delay_sec,
        idle_rounds: Number.isInteger(s.idle_rounds) ? s.idle_rounds : null,
        print_enabled: s.print_enabled !== false,
    };
}

// Адрес файла на сервере (запасной путь, когда локального кэша нет).
// ad_path приходит без ведущего слэша ("media/ads/…") — без нормализации
// склеивался бы в "…/apimedia/…".
export function remoteUrl(ad) {
    const base = process.env.REACT_APP_BASEURL || "";
    const path = String(ad?.ad_path || "").replace(/^\/+/, "");
    return `${base}/${path.split("/").map(encodeURIComponent).join("/")}`;
}

export const cacheKey = (ad) => ad?.version || ad?.ad_path;
